import React, { PropsWithChildren, ReactNode, createContext, use } from 'react';

import { numberHelper } from '@flipster/react/design-utils/formatted';
import { Styles, css } from '@flipster/styled-system/css';
import { token } from '@flipster/styled-system/tokens';
import { BigNumber } from '@flipster/utils/num';

type NumberType = BigNumber | string | number | undefined | null;
type ColorType = 'positive' | 'negative' | 'neutral';

interface FormattedResult {
  number: string;
  percent: string;
  color: ColorType;
}

/**
 * Profit and Loss Rules
 * @see https://www.figma.com/design/gwZidquUjZcCTDrfXJbqkN/Key-screens?node-id=1354-50479
 */
interface GetFormattedPnlOptions {
  precision?: number;
}
export const getFormattedPnl = (
  number: NumberType,
  percent: NumberType,
  options?: GetFormattedPnlOptions,
): FormattedResult => {
  const [numberLabel, { color }] = numberHelper(number, {
    precision: options?.precision ?? 2,
    toFixed: true,
    withPlus: true,
    withColor: true,
  });
  const [percentLabel] = numberHelper(percent, {
    precision: 2,
    toFixed: true,
    withPlus: true,
  });
  return {
    number: numberLabel,
    percent: percentLabel,
    color: color ?? 'neutral',
  };
};

interface PnlContext {
  number: string;
  percent: string;
}
const PnlContext = createContext<PnlContext>(null as unknown as PnlContext);
interface PnlBaseProps {
  number: NumberType;
  percent: NumberType;
  css?: Styles;
}
const PnlBase = ({ number, percent, css: cssProps, children }: PropsWithChildren<PnlBaseProps>) => {
  const { color, ...ctxt } = getFormattedPnl(number, percent);
  const colorStyle = {
    positive: token('colors.content.state.positive'),
    negative: token('colors.content.state.negative'),
    neutral: token('colors.content.default.level-5'),
  }[color];
  return (
    <PnlContext value={ctxt}>
      <div className={css(cssProps)} style={{ color: colorStyle }}>
        {children}
      </div>
    </PnlContext>
  );
};

interface PnlNumberProps {
  currency?: ReactNode;
  css?: Styles;
}
const PnlNumber = ({ currency, css: cssProps }: PnlNumberProps) => {
  const { number } = use(PnlContext);
  return (
    <span translate='no' className={css(cssProps)}>
      {number}
      {currency && <>&nbsp;{currency}</>}
    </span>
  );
};

interface PnlPercentProps {
  css?: Styles;
}
const PnlPercent = ({ css: cssProps }: PnlPercentProps) => {
  const { percent } = use(PnlContext);
  return (
    <span translate='no' className={css(cssProps)}>
      ({percent}%)
    </span>
  );
};

export const Pnl = Object.assign(PnlBase, { Number: PnlNumber, Percent: PnlPercent });
